import {
  useCallback,
  useMemo,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  MouseEventHandler,
} from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import BoxIcon from '@assets/images/box-icon.svg?react'
import MinusRectangleIcon from '@assets/images/minus-rectangle-icon.svg?react'
import ExpandIcon from '@assets/images/modal-expand-icon.svg?react'
import MinimizeIcon from '@assets/images/modal-minimize-icon.svg?react'
import PlusCircleIcon from '@assets/images/plus-circle-icon.svg?react'
import SalesforceIcon from '@assets/images/salesforce-icon.svg?react'
import Visibility from '@assets/images/text-visibility-on.svg?react'
import cn from 'classnames'
import genericSs from '../../../assets/styles/generic.module.scss'
import { getBoxFileId, isModifierClick } from '../../../helpers/boxFileLink'
import { handleStopPropagation } from '../../../helpers/helpers'
import {
  boxViewerModeFor,
  previewSyncRowProps,
  useOpenBoxPreview,
} from '../../../hooks/useBoxPreview'
import Icon, { IconSize } from '../Icon'
import IconButton from '../IconButton'
import styles from './Icons.module.scss'

// `.iconWrapper` imposes 24px on its svg (20px under `.iconWrapperSmall`), so an Icon inside
// it states that size rather than fighting it.
const wrapperIconSize = (size: 'small' | 'large'): IconSize => (size === 'small' ? 'md' : 'lg')

const WrapperLoader = ({ size }: { size: 'small' | 'large' }) => (
  <Icon name="loader" size={wrapperIconSize(size)} className={styles.rotating} />
)

/**
 * Some callers render one of these icons purely as a glyph inside a control they
 * already own — a `MenuItem`, a router `Link`. Emitting a <button> there would
 * nest one interactive element inside another (invalid HTML) and add a second,
 * duplicate tab stop for a single action. `decorative` renders a plain, aria-hidden
 * <span> with the same classes instead; the owning control keeps the click and the
 * accessible name. The union makes the two modes mutually exclusive at compile
 * time, so a decorative icon cannot be handed a no-op handler to "turn off".
 */
type DecorativeOr<THandler, TKey extends string> =
  | ({ decorative: true } & { [K in TKey]?: never })
  | ({ decorative?: false } & { [K in TKey]: THandler })

interface LinkProps {
  link: string
  title?: string
  size?: 'small' | 'large'
  className?: string
  disabled?: boolean
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void
  isLoading?: boolean
}

export const SalesforceLink = ({ link, title = 'Salesforce', size = 'large' }: LinkProps) => {
  return (
    <Tooltip title={title} placement="top">
      <Link
        className={styles.salesforceLinkIcon}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div
          className={cn(styles.iconWrapper, {
            [styles.iconWrapperSmall]: size === 'small',
          })}
        >
          <SalesforceIcon className={styles.salesforceLinkIcon} />
        </div>
      </Link>
    </Tooltip>
  )
}

export const WebsiteLink = ({
  link,
  onClick = handleStopPropagation,
  title = '',
  className = '',
  size = 'large',
  disabled = false,
  isLoading = false,
}: LinkProps) => {
  return (
    <Tooltip title={title} placement="top">
      <Link
        className={cn(styles.boxLinkIcon, className, {
          [genericSs.disabled]: disabled,
        })}
        href={link}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div
          className={cn(styles.iconWrapper, {
            [styles.iconWrapperActive]: isLoading,
            [styles.iconWrapperSmall]: size === 'small',
            [genericSs.disabled]: disabled,
          })}
        >
          {isLoading ? (
            <WrapperLoader size={size} />
          ) : (
            <Icon name="globe" size={wrapperIconSize(size)} />
          )}
        </div>
      </Link>
    </Tooltip>
  )
}

// A link to a Box file previews in place unless the caller handles the click itself; a
// folder link, or a modifier click, still opens Box.
export const BoxLink = ({
  link,
  onClick,
  title = '',
  className = '',
  size = 'large',
  disabled = false,
  isLoading = false,
}: LinkProps) => {
  const openPreview = useOpenBoxPreview()
  const isPreview = !onClick && !!getBoxFileId(link)

  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(event)
        return
      }
      event.stopPropagation()
      if (!isPreview || isModifierClick(event)) {
        return
      }
      event.preventDefault()
      void openPreview(link, boxViewerModeFor(event.currentTarget))
    },
    [onClick, isPreview, link, openPreview],
  )

  return (
    <Tooltip title={isPreview ? `Preview ${title || 'file'}` : title} placement="top">
      <Link
        className={cn(styles.boxLinkIcon, className, {
          [genericSs.disabled]: disabled,
        })}
        href={link}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        {...(isPreview ? previewSyncRowProps : undefined)}
      >
        <div
          className={cn(styles.iconWrapper, {
            [styles.iconWrapperActive]: isLoading,
            [styles.iconWrapperSmall]: size === 'small',
            [genericSs.disabled]: disabled,
          })}
        >
          {isLoading ? <WrapperLoader size={size} /> : <BoxIcon />}
        </div>
      </Link>
    </Tooltip>
  )
}

type EditMappingProps = {
  title?: string
  ariaLabel?: string
  mappingRequired?: boolean
  mappingDisabled?: boolean
  size?: 'small' | 'large'
} & DecorativeOr<() => void, 'action'>

export const EditMapping = ({
  action,
  title = 'Mapping',
  ariaLabel = 'Edit mapping',
  mappingRequired = false,
  mappingDisabled = false,
  size = 'large',
  decorative = false,
}: EditMappingProps) => {
  const onClick = useCallback(() => {
    if (mappingDisabled) {
      return
    }

    action?.()
  }, [action, mappingDisabled])

  const iconClassName = cn(styles.iconWrapper, styles.accentIconWrapper, {
    [styles.iconButtonReset]: !decorative,
    [styles.mappingRequired]: mappingRequired && !mappingDisabled,
    [styles.mappingDisabled]: mappingDisabled,
    [styles.iconWrapperSmall]: size === 'small',
  })
  const icon = <Icon name="grid-map" size={wrapperIconSize(size)} />

  return (
    <Tooltip title={title} placement="top">
      {decorative ? (
        <span data-cy="mapping-icon" aria-hidden className={iconClassName}>
          {icon}
        </span>
      ) : (
        <button
          type="button"
          data-cy="mapping-icon"
          aria-label={ariaLabel}
          // `mappingDisabled` is enforced by the early return above rather than the
          // native `disabled` attribute, which would drop the tooltip and the
          // not-allowed cursor. `aria-disabled` exposes the same state to AT.
          aria-disabled={mappingDisabled}
          className={iconClassName}
          onClick={onClick}
        >
          {icon}
        </button>
      )}
    </Tooltip>
  )
}

export const ExpandAndMinimize = ({
  action,
  isExpanded,
  className,
  size = 'large',
  disableTooltip = false,
  ariaLabel,
  ...rest
}: {
  action: (event?: ReactMouseEvent) => void
  isExpanded: boolean
  className?: string
  size?: 'small' | 'large'
  disableTooltip?: boolean
  ariaLabel?: string
  [key: string]: unknown
}) => {
  const title = useMemo(() => (isExpanded ? 'Minimize' : 'Expand'), [isExpanded])

  const iconElement = (
    <button
      type="button"
      aria-label={ariaLabel ?? title}
      aria-expanded={isExpanded}
      className={cn(styles.iconButtonReset, styles.iconWrapper, className, {
        [styles.iconWrapperSmall]: size === 'small',
      })}
      onClick={action}
      {...rest}
    >
      {isExpanded ? <MinimizeIcon /> : <ExpandIcon />}
    </button>
  )

  if (disableTooltip) {
    return iconElement
  }

  return (
    <Tooltip title={title} placement="top">
      {iconElement}
    </Tooltip>
  )
}

// Detailed/Condensed display toggle. Shares `ExpandAndMinimize`'s shape so it
// sits correctly beside it in table headers; the icon and tooltip describe the
// view being switched TO, matching that neighbour.
export const DensityToggle = ({
  action,
  isCondensed,
  className,
  size = 'large',
  ariaLabel = 'Change row density',
  ...rest
}: {
  action: (event?: ReactMouseEvent) => void
  isCondensed: boolean
  className?: string
  size?: 'small' | 'large'
  ariaLabel?: string
  [key: string]: unknown
}) => {
  const title = useMemo(() => (isCondensed ? 'Detailed view' : 'Condensed view'), [isCondensed])

  return (
    <Tooltip title={title} placement="top">
      <button
        type="button"
        aria-label={ariaLabel}
        className={cn(styles.iconButtonReset, styles.iconWrapper, className, {
          [styles.iconWrapperSmall]: size === 'small',
        })}
        onClick={action}
        {...rest}
      >
        <Icon
          name={isCondensed ? 'unfold-horizontal' : 'fold-horizontal'}
          size={wrapperIconSize(size)}
        />
      </button>
    </Tooltip>
  )
}

// type where either link or onClick is required
type ExternalLinkProps = {
  link?: string
  onClick?: () => void
  title?: string
  className?: string
  useOnClick?: boolean
  size?: 'small' | 'large'
  disableHover?: boolean
  // A Box file link previews in place, with an eye icon; a modifier click still opens Box.
  previewBoxFile?: boolean
  'data-cy'?: string
}

export const ExternalLink = ({
  link,
  title,
  className,
  useOnClick = false,
  size = 'large',
  disableHover = false,
  previewBoxFile = false,
  onClick,
  'data-cy': dataCy,
}: ExternalLinkProps) => {
  const openPreview = useOpenBoxPreview()
  const isPreview = previewBoxFile && !onClick && !!getBoxFileId(link)

  const handleOnClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (isPreview) {
        event.stopPropagation()
        if (isModifierClick(event)) {
          return
        }
        event.preventDefault()
        void openPreview(link, boxViewerModeFor(event.currentTarget))
        return
      }
      if ((link && useOnClick) || onClick) {
        event.stopPropagation()
        event.preventDefault()
        if (onClick) {
          onClick()
        } else {
          window.open(link, '_blank')
        }
      }
    },
    [isPreview, link, useOnClick, onClick, openPreview],
  )

  return (
    <Link
      data-cy={dataCy}
      color="primary"
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(className)}
      onClick={handleOnClick}
      {...(isPreview ? previewSyncRowProps : undefined)}
    >
      <Tooltip title={isPreview ? `Preview ${title || 'file'}` : title} placement="top">
        <div
          className={cn(styles.accentIconWrapper, {
            [styles.iconWrapperSmall]: size === 'small',
            [styles.iconWrapper]: !disableHover,
          })}
        >
          <Icon
            name={isPreview ? 'eye' : 'external-link'}
            size={disableHover ? 'lg' : wrapperIconSize(size)}
            className={styles.linkIcon}
          />
        </div>
      </Tooltip>
    </Link>
  )
}

export const RegenerateIcon = ({
  action,
  title = 'Refresh reports',
  ariaLabel = 'Regenerate',
  isLoading = false,
  size = 'large',
  disabled = false,
  dataCy,
}: {
  action: () => void
  title?: string
  ariaLabel?: string
  isLoading?: boolean
  size?: 'small' | 'large'
  disabled?: boolean
  dataCy?: string
}) => {
  return (
    <button
      type="button"
      data-cy={dataCy}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(styles.iconButtonReset, styles.iconWrapper, {
        [styles.iconWrapperActive]: isLoading,
        [styles.iconWrapperSmall]: size === 'small',
        [genericSs.disabled]: disabled,
      })}
      onClick={action}
    >
      <Tooltip title={title} placement="top">
        <Icon
          name="refresh"
          size={wrapperIconSize(size)}
          className={cn({ [styles.rotating]: isLoading })}
        />
      </Tooltip>
    </button>
  )
}

export const EditSourceData = ({
  action,
  error = false,
  size = 'large',
  ariaLabel = 'Edit source data',
}: {
  action: () => void
  error?: boolean
  size?: 'small' | 'large'
  ariaLabel?: string
}) => {
  return (
    <Tooltip title="Source data" placement="top">
      <button
        type="button"
        aria-label={ariaLabel}
        className={cn(styles.iconButtonReset, styles.iconWrapper, styles.accentIconWrapper, {
          [styles.error]: error,
          [styles.iconWrapperSmall]: size === 'small',
        })}
        onClick={action}
        data-snapshot-hide
      >
        <Icon name="data-source" size={wrapperIconSize(size)} />
      </button>
    </Tooltip>
  )
}

export const PDFIcon = ({
  title = 'View PDF',
  size = 'large',
}: {
  title?: string
  size?: 'small' | 'large'
}) => {
  return (
    <div
      className={cn(styles.iconWrapper, styles.accentIconWrapper, {
        [styles.iconWrapperSmall]: size === 'small',
      })}
    >
      <Tooltip title={title} placement="top">
        <Icon name="pdf" size={wrapperIconSize(size)} />
      </Tooltip>
    </div>
  )
}

// `ariaLabel` is required, not defaulted: this wrapper carries no meaning of its
// own, so only the caller knows what the button it renders actually does.
export const IconWrapper = ({
  title,
  action,
  children,
  size = 'large',
  ariaLabel,
}: {
  title: string
  action: () => void
  children: ReactNode
  size?: 'small' | 'large'
  ariaLabel: string
}) => {
  return (
    <Tooltip title={title} placement="top">
      <button
        type="button"
        aria-label={ariaLabel}
        className={cn(styles.iconButtonReset, styles.iconWrapper, {
          [styles.iconWrapperSmall]: size === 'small',
        })}
        onClick={action}
      >
        {children}
      </button>
    </Tooltip>
  )
}

export const ExpandDetailIcon = ({
  onClick,
  isExpanded,
  ariaLabel = 'Expand details',
}: {
  // The event is handed through so a chevron inside a clickable row can stop the
  // click bubbling up to it. Optional rather than required, and not stopped here:
  // DiligenceDocuments deliberately passes a no-op and lets its wrapper handle the
  // click, so swallowing the event for everyone would break that.
  onClick: (event?: ReactMouseEvent<HTMLButtonElement>) => void
  isExpanded: boolean
  ariaLabel?: string
}) => {
  // `box="xs"` is load bearing: almost every call site is a table cell, so the 32px
  // box would grow every such row.
  return (
    <IconButton
      title={ariaLabel}
      tooltipPlacement="top"
      color="primary"
      box="xs"
      aria-expanded={isExpanded}
      className={cn(genericSs.expandIcon, {
        [genericSs.expandIconOpen]: isExpanded,
      })}
      onClick={onClick}
      disableFocusRipple
      disableRipple
    >
      <Icon name="chevron-down" size="sm" />
    </IconButton>
  )
}

type EditIconProps = {
  title?: string
  ariaLabel?: string
  className?: string
  size?: 'small' | 'large'
  disabled?: boolean
  dataCy?: string
} & DecorativeOr<() => void, 'onClick'>

export const EditIcon = ({
  title = 'Edit',
  ariaLabel = 'Edit',
  onClick,
  className = '',
  size = 'large',
  disabled,
  dataCy,
  decorative = false,
}: EditIconProps) => {
  const iconClassName = cn(styles.iconWrapper, className, {
    [styles.iconButtonReset]: !decorative,
    [styles.iconWrapperSmall]: size === 'small',
    [genericSs.disabled]: disabled,
  })
  const icon = (
    <Tooltip title={title} placement="top">
      <Icon name="edit" size="lg" className={styles.pencilIcon} />
    </Tooltip>
  )

  if (decorative) {
    return (
      <span data-cy={dataCy} aria-hidden className={iconClassName}>
        {icon}
      </span>
    )
  }

  return (
    <button
      type="button"
      data-cy={dataCy}
      aria-label={ariaLabel}
      disabled={disabled}
      className={iconClassName}
      onClick={onClick}
    >
      {icon}
    </button>
  )
}

export const VisibilityIcon = ({
  action,
  isVisible,
  size = 'large',
  ariaLabel,
}: {
  action: () => void
  isVisible: boolean
  size?: 'small' | 'large'
  ariaLabel?: string
}) => {
  const title = useMemo(() => (isVisible ? 'Hide' : 'Show'), [isVisible])

  return (
    <Tooltip title={title} placement="top">
      <button
        type="button"
        aria-label={ariaLabel ?? title}
        aria-pressed={isVisible}
        className={cn(styles.iconButtonReset, styles.iconWrapper, styles.withoutHover, {
          [styles.iconWrapperSmall]: size === 'small',
        })}
        onClick={action}
      >
        {isVisible ? (
          <Visibility className={styles.visibilityOnIcon} />
        ) : (
          <Icon name="eye-off" size={wrapperIconSize(size)} />
        )}
      </button>
    </Tooltip>
  )
}

export const ShowAndHide = ({
  action,
  isExpanded,
  className,
  ariaLabel,
}: {
  action: (event?: ReactMouseEvent) => void
  isExpanded: boolean
  className?: string
  ariaLabel?: string
}) => {
  return (
    <button
      type="button"
      aria-label={ariaLabel ?? (isExpanded ? 'Hide' : 'Show')}
      aria-expanded={isExpanded}
      className={cn(styles.iconButtonReset, className)}
      onClick={action}
    >
      {isExpanded ? <MinusRectangleIcon /> : <PlusCircleIcon />}
    </button>
  )
}

export const ClearIcon = ({
  action,
  title = 'Clear',
  ariaLabel = 'Clear',
  isLoading = false,
  size = 'large',
  disabled = false,
}: {
  action: () => void
  title?: string
  ariaLabel?: string
  isLoading?: boolean
  size?: 'small' | 'large'
  disabled?: boolean
}) => {
  return (
    <Tooltip title={title} placement="top">
      <button
        type="button"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={action}
        className={cn(styles.iconButtonReset, styles.iconWrapper, {
          [styles.iconWrapperActive]: isLoading,
          [styles.iconWrapperSmall]: size === 'small',
          [genericSs.disabled]: disabled,
        })}
      >
        <span
          className={cn(styles.clearIcon, {
            [styles.clearIconLoading]: isLoading,
          })}
        >
          <Icon name="x" size="xs" />
        </span>
      </button>
    </Tooltip>
  )
}

export const CloseIcon = ({
  action,
  title = 'Close',
  ariaLabel = 'Close',
  isLoading = false,
  size = 'large',
}: {
  action: () => void
  title?: string
  ariaLabel?: string
  isLoading?: boolean
  size?: 'small' | 'large'
}) => {
  return (
    <Tooltip title={title} placement="top">
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={action}
        className={cn(styles.iconButtonReset, styles.iconWrapper, {
          [styles.iconWrapperActive]: isLoading,
          [styles.clearIconLoading]: isLoading,
          [styles.iconWrapperSmall]: size === 'small',
        })}
      >
        <Icon name="x" size="lg" />
      </button>
    </Tooltip>
  )
}

export const MenuIcon = ({
  className,
  onClick,
  isActive,
  isLoading,
  size = 'small',
  title = 'More actions',
  ariaLabel = 'Open menu',
  dataCy,
  id,
}: {
  className?: string
  onClick: MouseEventHandler<HTMLButtonElement>
  isActive?: boolean
  isLoading?: boolean
  size?: 'small' | 'large'
  title?: string
  ariaLabel?: string
  dataCy?: string
  /** Set when the icon is the trigger of a menu that labels itself from it. */
  id?: string
}) => {
  return (
    <IconButton
      title={title}
      tooltipPlacement="top"
      box="sm"
      id={id}
      data-cy={dataCy}
      aria-label={ariaLabel}
      className={cn(styles.iconWrapper, styles.iconButtonWrapper, className, {
        [styles.iconWrapperActive]: isActive || isLoading,
        [styles.iconWrapperSmall]: size === 'small',
      })}
      disableRipple
      onClick={onClick}
    >
      <Icon name="more-vertical" size={wrapperIconSize(size)} />
    </IconButton>
  )
}

export const SettingsIcon = ({
  className,
  link,
  title = 'Settings',
  size = 'large',
  dataCy,
}: {
  className?: string
  link?: string
  title?: string
  size?: 'small' | 'large'
  dataCy?: string
}) => {
  return (
    <Link
      className={cn(styles.iconWrapper, styles.iconButtonWrapper, className, {
        [styles.iconWrapperSmall]: size === 'small',
      })}
      color="inherit"
      underline="none"
      component={RouterLink}
      to={link}
      data-cy={dataCy}
    >
      <Tooltip title={title} placement="top">
        <Icon name="settings" size={wrapperIconSize(size)} />
      </Tooltip>
    </Link>
  )
}

export const SendIcon = ({
  isLoading,
  disabled,
  className,
  onClick,
  title = 'Send',
  ariaLabel = 'Send',
  size = 'large',
}: {
  isLoading?: boolean
  disabled?: boolean
  className?: string
  onClick: MouseEventHandler<HTMLButtonElement>
  title?: string
  ariaLabel?: string
  size?: 'small' | 'large'
}) => {
  return (
    <IconButton
      title={title}
      tooltipPlacement="top"
      box="sm"
      disableRipple
      aria-label={ariaLabel}
      className={cn(styles.iconWrapper, styles.iconButtonWrapper, className, {
        [styles.iconWrapperActive]: isLoading,
        [genericSs.disabled]: disabled,
        [styles.iconWrapperSmall]: size === 'small',
      })}
      onClick={onClick}
      disabled={disabled}
    >
      {isLoading ? (
        <WrapperLoader size={size} />
      ) : (
        <Icon name="send" size={wrapperIconSize(size)} className={styles.accentIcon} />
      )}
    </IconButton>
  )
}

export const ReceiveIcon = ({
  isLoading,
  disabled,
  className,
  onClick,
  title = 'Receive',
  ariaLabel = 'Receive',
  size = 'large',
}: {
  isLoading?: boolean
  disabled?: boolean
  className?: string
  onClick: MouseEventHandler<HTMLButtonElement>
  title?: string
  ariaLabel?: string
  size?: 'small' | 'large'
}) => {
  return (
    <IconButton
      title={title}
      tooltipPlacement="top"
      box="sm"
      disableRipple
      aria-label={ariaLabel}
      className={cn(styles.iconWrapper, styles.iconButtonWrapper, className, {
        [styles.iconWrapperActive]: isLoading,
        [genericSs.disabled]: disabled,
        [styles.iconWrapperSmall]: size === 'small',
      })}
      onClick={onClick}
      disabled={disabled}
    >
      {isLoading ? (
        <WrapperLoader size={size} />
      ) : (
        // Colored on a box-less span: a class on the IconButton loses to MUI's disabled color.
        <span className={cn(genericSs.displayContents, styles.accentIconWrapper)}>
          <Icon name="deposit" size={wrapperIconSize(size)} />
        </span>
      )}
    </IconButton>
  )
}

export const AddIcon = ({
  isLoading,
  disabled,
  className,
  onClick,
  title = 'Add',
  ariaLabel = 'Add',
  size = 'large',
}: {
  isLoading?: boolean
  disabled?: boolean
  className?: string
  onClick: MouseEventHandler<HTMLButtonElement>
  title?: string
  ariaLabel?: string
  size?: 'small' | 'large'
}) => {
  return (
    <IconButton
      title={title}
      tooltipPlacement="top"
      box="sm"
      disableRipple
      aria-label={ariaLabel}
      className={cn(styles.iconWrapper, styles.iconButtonWrapper, styles.plusIcon, className, {
        [styles.iconWrapperActive]: isLoading,
        [genericSs.disabled]: disabled,
        [styles.iconWrapperSmall]: size === 'small',
      })}
      onClick={onClick}
      disabled={disabled}
    >
      {isLoading ? (
        <WrapperLoader size={size} />
      ) : (
        <Icon name="plus" size={wrapperIconSize(size)} />
      )}
    </IconButton>
  )
}

export const DeleteIcon = ({
  isLoading,
  disabled,
  className,
  onClick,
  title = 'Delete',
  ariaLabel = 'Delete',
  size = 'large',
}: {
  isLoading?: boolean
  disabled?: boolean
  className?: string
  onClick: MouseEventHandler<HTMLButtonElement>
  title?: string
  ariaLabel?: string
  size?: 'small' | 'large'
}) => {
  return (
    <IconButton
      title={title}
      tooltipPlacement="top"
      box="sm"
      disableRipple
      aria-label={ariaLabel}
      className={cn(styles.iconWrapper, styles.iconButtonWrapper, className, {
        [styles.iconWrapperActive]: isLoading,
        [genericSs.disabled]: disabled,
        [styles.iconWrapperSmall]: size === 'small',
      })}
      onClick={onClick}
      disabled={disabled}
    >
      {isLoading ? (
        <WrapperLoader size={size} />
      ) : (
        <Icon name="trash" size="lg" className={styles.deleteIcon} />
      )}
    </IconButton>
  )
}
